/**
 * Helper to remember dismissed alerts/banners in localStorage, so a user who closed an
 * alert is not bothered with it again for a while. Reusable for any alert: pass a unique
 * storage key per alert.
 *
 * The stored value is the dismissal timestamp (ms since epoch); the suppression window is
 * evaluated at read time via {@link isAlertDismissed}.
 */

/** Default duration an alert stays dismissed after being closed, in minutes (12 hours). */
export const DEFAULT_ALERT_DISMISS_MINUTES = 12 * 60;

/**
 * Marks an alert as dismissed (stores the current timestamp under the given key).
 *
 * @param storageKey Unique localStorage key identifying the alert (e.g.
 *   "squeeze.validation.X.ignoreAlert").
 */
export const dismissAlert = (storageKey: string): void => {
	try {
		localStorage.setItem(storageKey, String(Date.now()));
	} catch (error) {
		// localStorage may be unavailable (private mode / quota exceeded / serverside rendered ) — ignore
	}
};

/**
 * Whether an alert is currently dismissed, i.e. it was closed less than `minutes` ago.
 * Expired entries are removed so storage does not accumulate stale keys.
 *
 * @param storageKey Unique localStorage key identifying the alert.
 * @param minutes Suppression window in minutes (default 12 hours). Pass another value to use a different duration.
 */
export const isAlertDismissed = (storageKey: string, minutes: number = DEFAULT_ALERT_DISMISS_MINUTES): boolean => {
	try {
		const dismissedAt = Number(localStorage.getItem(storageKey));
		if (!dismissedAt || Date.now() - dismissedAt >= minutes * 60 * 1000) {
			localStorage.removeItem(storageKey);
			return false;
		}
		return true;
	} catch (error) {
		return false;
	}
};
